//****************************************************************************
//
// Copyright(C) 2025, CyberTAN Technology, Inc. All Rights Reserved.
// This source code contains confidential and proprietary
// information of CyberTAN Technology, Inc. Unauthorized reproduction,
// modification, distribution, or disclosure of this file, in whole or in part,
// by any means, is strictly prohibited without prior written permission from
// CyberTAN Technology, Inc.
//
// THIS SOURCE CODE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
// EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NONINFRINGEMENT.
//
// IN NO EVENT SHALL CYBERTAN TECHNOLOGY, INC. BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
// TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOURCE CODE.
//
//****************************************************************************

// 統一管理全站所有 SSE 連線 (type 例如 'SSE'、'HW_TRIGGER'、'FLASH_STATUS')。
// 這個模組把連線策略、領袖選舉、心跳/TTL 統一成一份,
// 所有消費者都透過 subscribe() 取得資料,不再各自維護連線狀態。
type SseHandler = (data: any) => void;

interface TypeEntry {
  url: string;
  handlers: Set<SseHandler>;
  eventSource?: EventSource; // 直連模式 / BroadcastChannel 模式下 Leader 持有的上游連線
}

type ConnectionMode = 'shared-worker' | 'broadcast-channel' | 'direct';

const WORKER_URL = '/sse-shared-worker.js';
const CHANNEL_NAME = 'sse_connection_manager_channel';
const LEADER_LOCK_KEY = 'sse_connection_manager_leader_lock';
const LEADER_LOCK_TTL = 5000; // Lock 逾時時間 (ms):超過此時間未更新心跳,視為 Leader 已死亡 (含分頁崩潰/被強制關閉)
const LEADER_HEARTBEAT_INTERVAL = 2000; // Leader 心跳更新間隔 (ms),須小於 LEADER_LOCK_TTL

class SseConnectionManager {
  private registry = new Map<string, TypeEntry>();
  private mode: ConnectionMode | null = null;

  // SharedWorker 模式 (優先方案):全站只會有這一個 Port
  private sharedWorker: SharedWorker | null = null;

  // BroadcastChannel fallback 模式:全站只會有這一個頻道 + 一套領袖選舉狀態
  private channel: BroadcastChannel | null = null;
  private isLeader = false;
  private tabId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private electionTimer: ReturnType<typeof setInterval> | null = null;

  /**
   * 訂閱某個 SSE type 的資料。回傳 unsubscribe function,呼叫它即可取消訂閱
   * (該 type 已無其他訂閱者時,會關閉對應的上游連線)。
   */
  subscribe(type: string, url: string, handler: SseHandler): () => void {
    let entry = this.registry.get(type);
    if (!entry) {
      entry = { url, handlers: new Set() };
      this.registry.set(type, entry);
    } else {
      entry.url = url; // 理論上同一個 type 的 url 在生命週期內固定,這裡僅作保險
    }
    entry.handlers.add(handler);

    this.ensureMode();
    this.ensureConnection(type, entry);

    return () => this.unsubscribe(type, handler);
  }

  private unsubscribe(type: string, handler: SseHandler) {
    const entry = this.registry.get(type);
    if (!entry) return;
    entry.handlers.delete(handler);
    if (entry.handlers.size > 0) return;

    // 已無人訂閱此 type,關閉對應連線
    if (this.mode === 'shared-worker') {
      this.sharedWorker?.port.postMessage({ command: 'close', type });
    } else {
      entry.eventSource?.close();
    }
    this.registry.delete(type);
  }

  private dispatch(type: string, data: any) {
    this.registry.get(type)?.handlers.forEach((handler) => handler(data));
  }

  // ---- 連線模式判定 (全站只判定一次,所有 type 共用同一個模式) ----

  private ensureMode() {
    if (this.mode) return;

    if (typeof window.SharedWorker !== 'undefined') {
      try {
        this.sharedWorker = new SharedWorker(WORKER_URL);
        this.sharedWorker.port.start();
        this.sharedWorker.port.onmessage = (event: MessageEvent) => {
          const { sourceType, data } = event.data;
          this.dispatch(sourceType, data);
        };
        this.mode = 'shared-worker';
        return;
      } catch (e) {
        console.warn('[SSE] SharedWorker fail, trying BroadcastChannel');
      }
    }

    if (typeof window.BroadcastChannel !== 'undefined') {
      this.mode = 'broadcast-channel';
      this.setupBroadcastChannel();
      return;
    }

    this.mode = 'direct';
  }

  private ensureConnection(type: string, entry: TypeEntry) {
    if (this.mode === 'shared-worker') {
      this.sharedWorker?.port.postMessage({ command: 'start', type, url: entry.url });
      return;
    }

    if (this.mode === 'broadcast-channel') {
      // 是 Leader 才需要真正建立連線;Follower 只需等待 Leader 廣播
      if (this.isLeader) this.openLeaderEventSource(type, entry);
      return;
    }

    // 直連模式:每個分頁各自連線,不跨分頁去重 (與過去行為一致)
    if (!entry.eventSource) {
      entry.eventSource = new EventSource(entry.url);
      entry.eventSource.onmessage = (e) => this.dispatch(type, JSON.parse(e.data));
    }
  }

  // ---- BroadcastChannel + Leader Election (心跳/TTL,避免分頁崩潰造成 Lock 永久殘留) ----

  private setupBroadcastChannel() {
    this.channel = new BroadcastChannel(CHANNEL_NAME);
    this.channel.onmessage = (e) => {
      if (e.data?.from === 'LEADER') this.dispatch(e.data.sourceType, e.data.data);
    };

    window.addEventListener('beforeunload', () => this.releaseLeaderLock());

    this.tryBecomeLeader();

    // 尚未成為 Leader 時,定期檢查 Lock 是否逾時 (例如原 Leader 分頁崩潰未清除 Lock),嘗試接管
    this.electionTimer = setInterval(() => {
      if (!this.isLeader) this.tryBecomeLeader();
    }, LEADER_LOCK_TTL);
  }

  private tryBecomeLeader() {
    const currentLockRaw = localStorage.getItem(LEADER_LOCK_KEY);
    let isLockStale = true;
    if (currentLockRaw) {
      try {
        const { ts } = JSON.parse(currentLockRaw);
        isLockStale = Date.now() - ts > LEADER_LOCK_TTL;
      } catch {
        isLockStale = true;
      }
    }
    if (!isLockStale) return;

    localStorage.setItem(LEADER_LOCK_KEY, JSON.stringify({ id: this.tabId, ts: Date.now() }));

    // 寫入後延遲二次確認,避免多個分頁同時偵測到逾時而搶占 Leader (race condition)
    setTimeout(
      () => {
        if (this.isLeader) return;
        const confirmRaw = localStorage.getItem(LEADER_LOCK_KEY);
        let won = false;
        try {
          won = !!confirmRaw && JSON.parse(confirmRaw).id === this.tabId;
        } catch {
          won = false;
        }
        if (!won) return; // 搶占失敗,維持 Follower,等待下一次檢查

        this.isLeader = true;

        // 接管當下,把目前已存在的訂閱 (例如掛載時就訂閱的 'SSE'、'HW_TRIGGER') 全部補開連線
        this.registry.forEach((entry, subscribedType) => this.openLeaderEventSource(subscribedType, entry));

        if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
        this.heartbeatTimer = setInterval(() => {
          localStorage.setItem(LEADER_LOCK_KEY, JSON.stringify({ id: this.tabId, ts: Date.now() }));
        }, LEADER_HEARTBEAT_INTERVAL);

        if (this.electionTimer) {
          clearInterval(this.electionTimer);
          this.electionTimer = null;
        }
      },
      Math.random() * 100 + 50
    );
  }

  private openLeaderEventSource(type: string, entry: TypeEntry) {
    if (entry.eventSource) return;
    entry.eventSource = new EventSource(entry.url);
    entry.eventSource.onmessage = (e) => {
      const parsed = JSON.parse(e.data);
      this.dispatch(type, parsed);
      this.channel?.postMessage({ from: 'LEADER', sourceType: type, data: parsed });
    };
  }

  // 釋放 Leader 身份與相關計時器。崩潰/被強制關閉時不會觸發 beforeunload,
  // 但 Lock 仍會因心跳停止而於 LEADER_LOCK_TTL 後被其他分頁判定逾時並接管,不會永久卡死。
  private releaseLeaderLock() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (this.electionTimer) {
      clearInterval(this.electionTimer);
      this.electionTimer = null;
    }
    if (this.isLeader) {
      localStorage.removeItem(LEADER_LOCK_KEY);
      this.isLeader = false;
    }
  }
}

export const sseConnectionManager = new SseConnectionManager();
